import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// --- ResizeObserver loop suppression (Radix Select / Recharts trigger this) ---
// The browser fires a harmless "ResizeObserver loop completed with undelivered
// notifications" warning when an observed element resizes mid-callback. CRA's
// dev overlay treats it as a fatal error and floods the screen with a red panel.
// Fix (1): break the loop by deferring observer callbacks one frame.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const NativeRO = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends NativeRO {
    constructor(callback) {
      let rAF = 0;
      super((entries, observer) => {
        if (rAF) cancelAnimationFrame(rAF);
        rAF = requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

// Fix (2): defence in depth — if the message still leaks (some third-party
// libraries construct the native ResizeObserver directly), stop it from
// reaching CRA's error overlay.
const swallowResizeObserverLoopError = (e) => {
  const msg = e?.message || e?.reason?.message || "";
  if (typeof msg === "string" && msg.includes("ResizeObserver loop")) {
    e.stopImmediatePropagation?.();
    e.preventDefault?.();
    return true;
  }
};
window.addEventListener("error", swallowResizeObserverLoopError, true);
window.addEventListener("unhandledrejection", swallowResizeObserverLoopError, true);

// Fix (3): CRA's dev overlay in webpack-dev-server subscribes via its own
// listener chain; window.onerror runs before the overlay wires up so we also
// nuke the string there. Returning true from onerror tells the browser the
// error has been handled and stops further propagation.
const _origOnError = window.onerror;
window.onerror = function (msg, ...rest) {
  if (typeof msg === "string" && msg.includes("ResizeObserver loop")) return true;
  return _origOnError ? _origOnError.call(this, msg, ...rest) : false;
};

// Suppress Recharts' first-render "width(-1) height(-1)" console.warn.
// This warning fires synchronously from ResponsiveContainer's initial render
// (before componentDidMount reads real dimensions). It cannot be avoided by
// wrapping/gating strategies. Confirmed by 3 testing iterations (35/36/37).
// The warning is dev-only cosmetic noise — charts render correctly.
const _origConsoleWarn = console.warn.bind(console);
console.warn = (...args) => {
  const first = args[0];
  if (typeof first === "string" && first.includes("The width(") && first.includes("height(")) {
    return; // benign Recharts init warning
  }
  _origConsoleWarn(...args);
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register PWA service worker (production and preview builds only — dev doesn't serve /service-worker.js reliably)
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .catch(() => { /* Ignore: dev server may not serve SW */ });
  });
}
